import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  IconButton,
  Button
} from '@mui/material';
import { ChevronLeft, ChevronRight, PlayArrow, PictureAsPdfOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ProductCarousel = ({ videos = [] }) => {
  // "videos" est le nom historique de la prop, mais le contenu est une liste de produits
  const products = Array.isArray(videos) ? videos : [];
  const navigate = useNavigate();
  const handleProductClick = (product) => {
    navigate(`/article/${product.id}`);
  };
  const showNavigation = products.length > 4;

  if (products.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          bgcolor: 'rgba(67, 172, 217, 0.05)',
          borderRadius: 2,
          border: '1px dashed rgba(67, 172, 217, 0.3)'
        }}
      >
        <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
          Aucun produit disponible
        </Typography>
        <Typography variant="body2" sx={{ color: '#999' }}>
          Les produits apparaîtront ici une fois que le profil pédagogique sera défini.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: 'auto',
          pb: 2,
          scrollSnapType: 'x mandatory',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '10px',
          }
        }}
      >
        {products.map((product, index) => (
          <Card
            key={`${product.id}-${index}`}
            sx={{
              minWidth: 200,
              maxWidth: 250,
              borderRadius: 3,
              boxShadow: '0px 4px 15px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              scrollSnapAlign: 'start',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
              }
            }}
            onClick={() => handleProductClick(product)}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Box sx={{ position: 'relative', mb: 1 }}>
                <img
                  src={product.image_url || 'https://via.placeholder.com/150x150?text=Produit'}
                  alt={product.title}
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '10px'
                  }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/150x150?text=Image+non+disponible';
                  }}
                />

                <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                  {product.is_free && (
                    <Chip
                      label="GRATUIT"
                      size="small"
                      sx={{
                        bgcolor: '#4CAF50',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem'
                      }}
                    />
                  )}
                  {!product.is_free && product.product_type === 'virtual' && (
                    <Chip
                      label="PREMIUM"
                      size="small"
                      sx={{
                        bgcolor: '#FF9800',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem'
                      }}
                    />
                  )}
                </Box>

                <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Chip
                    label={product.product_type === 'physical' ? 'Physique' : 'Virtuel'}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      fontSize: '0.6rem'
                    }}
                  />
                  {product.product_type === 'virtual' && product.virtual_type && (
                    <Chip
                      icon={product.virtual_type === 'video' ? <PlayArrow sx={{ fontSize: '0.7rem' }} /> : <PictureAsPdfOutlined sx={{ fontSize: '0.7rem' }} />}
                      label={product.virtual_type === 'video' ? 'VIDÉO' : 'PDF'}
                      size="small"
                      sx={{
                        bgcolor: product.virtual_type === 'video' ? '#FF5722' : '#2196F3',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem'
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: '#333',
                  mb: 1,
                  fontFamily: 'Achiko',
                  fontSize: '0.95rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {product.title}
              </Typography>

              {product.product_type === 'physical' && (
                <Typography
                  variant="body2"
                  sx={{
                    color: product.is_free ? '#4CAF50' : '#784e3c',
                    fontWeight: 'bold',
                    bgcolor: product.is_free ? 'rgba(76, 175, 80, 0.1)' : 'rgba(67, 172, 217, 0.1)',
                    borderRadius: '15px',
                    py: 0.5,
                    px: 2,
                    display: 'inline-block'
                  }}
                >
                  {product.is_free ? 'Gratuit' : `${product.price} FCFA`}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {showNavigation && (
        <Typography variant="body2" sx={{ textAlign: 'center', color: '#666' }}>
          Faites défiler horizontalement pour découvrir tous les produits ➡️
        </Typography>
      )}
    </Box>
  );
};

export default ProductCarousel;
